import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { getResultCacheWindow } from '../../common/services/resultCacheApi';
import { useAnalytics } from '../../../../core/analytics';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';

/** Preview shows the first few columns only — enough to recognize the row. */
const PREVIEW_COLUMN_COUNT = 5;
/** Hard cap per preview value so one LOB cell can't bloat the DOM. */
const PREVIEW_VALUE_MAX_CHARS = 200;

type SkippedRowPreview =
  | { status: 'loading' }
  | { status: 'loaded'; row: Record<string, unknown> }
  /** The single-row fetch failed — the row is STILL unreadable, even one-by-one. */
  | { status: 'error' };

interface SkippedRowsPanelProps {
  /** 0-based absolute indexes of the rows the CSV export could not read. */
  skippedRows: number[];
  /** Result-cache URI of the (still open) exported result set. */
  cacheUri: string | undefined;
  /** Columns of the exported result set — the first few shape the preview. */
  columns: { id?: string; name: string }[];
  /** True while the Skipped-rows bottom tab is the active one — first activation starts the lazy fetch. */
  active: boolean;
  /** Current source generation (sort/filter) — sent with each window fetch. */
  sourceGeneration: number;
  /** Row clicked anywhere — the owner activates the Results tab and centers the row. */
  onRowJump: (rowIndex0: number) => void;
}

/**
 * "Skipped rows (N)" bottom-tab content for the Query Editor (S2 follow-up):
 * one row per row a CSV "Export All" had to skip, as a lightweight table
 * (modeled on `PeekResultsPanel` — deliberately NOT a second `ResultsGrid`).
 * Each entry shows the 1-based row number plus a lazily fetched single-row
 * preview; the previews are fetched SEQUENTIALLY on first tab activation —
 * these are exactly the rows that stalled the export, so a parallel burst
 * against a struggling server is the one thing this panel must not do.
 * Post-export the one-row fetches usually succeed; a row that STILL cannot be
 * read gets a "Still unreadable" badge with a per-row Retry. Clicking a row
 * jumps the results grid to it (the owner drives the grid's controlled
 * selection). The panel lives only as long as the exported result set — the
 * owner clears it on re-run / result-set switch (see `QueryEditorTab.tsx`).
 */
export function SkippedRowsPanel({
  skippedRows,
  cacheUri,
  columns,
  active,
  sourceGeneration,
  onRowJump,
}: SkippedRowsPanelProps) {
  const { track } = useAnalytics();
  const [previews, setPreviews] = useState<Record<number, SkippedRowPreview>>({});
  // Latches on first activation so the sequential fetch runs exactly once —
  // leaving and re-entering the tab must not refetch rows already loaded.
  const [fetchStarted, setFetchStarted] = useState(false);
  useEffect(() => {
    if (active) setFetchStarted(true);
  }, [active]);

  // Read at fire time, not closed over: a sort/filter between activation and a
  // Retry click must not re-run the whole fetch effect.
  const sourceGenerationRef = useRef(sourceGeneration);
  sourceGenerationRef.current = sourceGeneration;

  const fetchRowPreview = useCallback(
    async (rowIndex: number): Promise<SkippedRowPreview> => {
      if (!cacheUri) return { status: 'error' };
      try {
        const res = await getResultCacheWindow({
          uri: cacheUri,
          startIndex: rowIndex,
          count: 1,
          expectedGeneration: sourceGenerationRef.current,
        });
        const row = res.rows[0];
        return row ? { status: 'loaded', row } : { status: 'error' };
      } catch {
        return { status: 'error' };
      }
    },
    [cacheUri],
  );

  // Sequential lazy fetch: one row at a time, in skip order.
  useEffect(() => {
    if (!fetchStarted) return;
    let cancelled = false;
    (async () => {
      let readable = 0;
      let unreadable = 0;
      for (const rowIndex of skippedRows) {
        if (cancelled) return;
        setPreviews((prev) => ({ ...prev, [rowIndex]: { status: 'loading' } }));
        const preview = await fetchRowPreview(rowIndex);
        if (cancelled) return;
        if (preview.status === 'loaded') readable++; else unreadable++;
        setPreviews((prev) => ({ ...prev, [rowIndex]: preview }));
      }
      // Diagnostic aggregate, once per panel (the panel remounts per export):
      // skipped rows that ARE readable moments later point at export-time
      // transport congestion (our side); rows still unreadable point at the
      // row/server. Buckets only — no counts, no indexes.
      track('feature:export_skipped_rows_preview', {
        grid: 'query-editor',
        readableAfterExport: unreadable === 0 ? 'all' : readable === 0 ? 'none' : 'some',
      });
    })();
    return () => {
      cancelled = true;
    };
    // skippedRows is set once per export (the panel is remounted for the next
    // one) — the fetch keys on activation, not on array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStarted, fetchRowPreview]);

  const handleRetry = useCallback(
    async (rowIndex: number) => {
      track('feature:export_skipped_row_retry', { grid: 'query-editor' });
      setPreviews((prev) => ({ ...prev, [rowIndex]: { status: 'loading' } }));
      const preview = await fetchRowPreview(rowIndex);
      setPreviews((prev) => ({ ...prev, [rowIndex]: preview }));
    },
    [fetchRowPreview, track],
  );

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      track('feature:export_skipped_row_jump', { grid: 'query-editor', source: 'tab' });
      onRowJump(rowIndex);
    },
    [onRowJump, track],
  );

  const previewColumns = columns.slice(0, PREVIEW_COLUMN_COUNT);

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="skipped-rows-panel">
      <div
        className="flex items-start gap-1.5 px-3 py-1.5 text-[11px] shrink-0
          bg-amber-500/10 border-b border-amber-500/30 text-amber-900 dark:text-amber-200"
      >
        <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
        <span>
          These rows could not be read during the CSV export and are missing from the file.
          Click a row to show it in the results grid.
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
            <tr>
              <th className="px-2 py-1 text-right font-medium text-muted-foreground border-b border-r border-border whitespace-nowrap w-px">
                Row
              </th>
              {previewColumns.map((col, i) => (
                <th
                  key={col.id ?? col.name ?? i}
                  className="px-2 py-1 text-left font-medium text-muted-foreground border-b border-r border-border whitespace-nowrap"
                >
                  {col.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {skippedRows.map((rowIndex) => {
              const preview = previews[rowIndex];
              return (
                <tr
                  key={rowIndex}
                  className="hover:bg-muted/30 cursor-pointer"
                  data-testid={`skipped-row-${rowIndex}`}
                  onClick={() => handleRowClick(rowIndex)}
                >
                  <td className="px-2 py-0.5 border-b border-r border-border/50 text-right tabular-nums whitespace-nowrap">
                    {(rowIndex + 1).toLocaleString()}
                  </td>
                  {preview?.status === 'loaded' ? (
                    previewColumns.map((col, colIdx) => {
                      const val = preview.row[col.id ?? col.name];
                      return (
                        <td
                          key={colIdx}
                          className="px-2 py-0.5 border-b border-r border-border/50 whitespace-nowrap max-w-[200px] truncate"
                        >
                          {val == null ? (
                            <span className="text-muted-foreground/50 italic">NULL</span>
                          ) : (
                            String(val).slice(0, PREVIEW_VALUE_MAX_CHARS)
                          )}
                        </td>
                      );
                    })
                  ) : (
                    <td
                      className="px-2 py-0.5 border-b border-border/50"
                      colSpan={Math.max(previewColumns.length, 1)}
                    >
                      {preview?.status === 'error' ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]
                              bg-amber-500/10 text-amber-900 dark:text-amber-200"
                            data-testid={`skipped-row-unreadable-${rowIndex}`}
                          >
                            <AlertTriangle className="h-3 w-3" />
                            Still unreadable
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]
                              text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            data-testid={`skipped-row-retry-${rowIndex}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRetry(rowIndex);
                            }}
                          >
                            <RotateCw className="h-3 w-3" />
                            Retry
                          </button>
                        </span>
                      ) : preview?.status === 'loading' ? (
                        <RandomDotmSpinner size={12} className="text-muted-foreground" />
                      ) : (
                        <span className="text-muted-foreground/50 italic">…</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
